import type { Language } from '@offer-catalog/services/i18n'
import type { ChipFamilyType } from './Families'
import { VpIcon } from '@vtmn-play/react/headless'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/Modal'
import { FamilyChip } from './FamilyChip'
import './families-modal.scss'

interface GroupedFamilies {
  subdepartmentId: number
  subdepartmentLabel: string
  chips: ChipFamilyType[]
}

export interface FamiliesModalProps {
  families: ChipFamilyType[]
  openModal: boolean
  validate: (families: ChipFamilyType[]) => void
  setOpenModal: (status: boolean) => void
}

interface FamiliesChipsProps {
  chips: ChipFamilyType[]
  currentLanguage: Language
  onRemove: (id: number) => void
}

function FamiliesChips({ chips, currentLanguage, onRemove }: Readonly<FamiliesChipsProps>) {
  return (
    <div className="modal-families-chips" data-testid="modal-families-chips">
      {chips.map(family => (
        <FamilyChip
          key={family.id}
          id={family.id}
          label={family.labels[currentLanguage]}
          remove={() => onRemove(family.id)}
        />
      ))}
    </div>
  )
}

function groupFamiliesBySubdepartment(families: ChipFamilyType[], currentLanguage: Language): GroupedFamilies[] {
  return Object.values(
    families.reduce((acc, chip) => {
      const key = chip.subdepartmentId
      if (!acc[key]) {
        acc[key] = {
          subdepartmentId: chip.subdepartmentId,
          subdepartmentLabel: chip.subdepartmentLabels[currentLanguage],
          chips: [],
        }
      }
      acc[key].chips.push(chip)
      return acc
    }, {} as Record<number, GroupedFamilies>),
  ).sort((a, b) => a.subdepartmentLabel.localeCompare(b.subdepartmentLabel))
}

export function FamiliesModal({
  families,
  openModal,
  validate,
  setOpenModal,
}: Readonly<FamiliesModalProps>) {
  const { i18n, t } = useTranslation()

  const [idsToRemove, setIdsToRemove] = useState<number[]>([])

  const currentLanguage = i18n.language.split('-')[0] as Language

  const filteredFamilies = useMemo(
    () => families.filter(family => !idsToRemove.includes(family.id)),
    [families, idsToRemove],
  )

  const groupedFamilies = useMemo(
    () => groupFamiliesBySubdepartment(filteredFamilies, currentLanguage),
    [filteredFamilies, currentLanguage],
  )

  return (
    <Modal
      dataTestid="families-selection-modal"
      isOpen={openModal}
      title={`${filteredFamilies.length} ${t('families-selected')}`}
      width="60vw"
      resetDisabled={filteredFamilies.length === 0}
      resetAction={() => setIdsToRemove(filteredFamilies.map(family => family.id))}
      cancelAction={() => {
        setOpenModal(false)
        setIdsToRemove([])
      }}
      validationAction={() => {
        validate(filteredFamilies)
        setOpenModal(false)
        setIdsToRemove([])
      }}
      onClose={() => {
        setOpenModal(false)
        setIdsToRemove([])
      }}
    >
      <div
        className="modal-families-chips-container"
        data-testid="modal-families-chips-container"
      >
        {filteredFamilies.length > 0
          ? (
              groupedFamilies.map(group => (
                <section key={group.subdepartmentId}>
                  <div className="modal-subdep-label" data-testid="modal-subdep-label">
                    {`${group.subdepartmentLabel} - ${group.subdepartmentId}`}
                  </div>
                  <FamiliesChips
                    chips={group.chips}
                    currentLanguage={currentLanguage}
                    onRemove={id => setIdsToRemove(prev => [...prev, id])}
                  />
                </section>
              ))
            )
          : (
              <span className="empty-message">
                <VpIcon name="information" />
                {t('products-page.reset-families-confirmation')}
              </span>
            )}
      </div>
    </Modal>
  )
}
